/**
 * Legacy Fallbacks Requiring Assignment — operational worklist (Jul 2026).
 *
 * Lists PA jurisdictions that still resolve collectors from legacy
 * directory data because no active collector assignment exists.
 * Feeds the controlled migration to official assignments — records are
 * verified and converted individually via the existing Create
 * Assignment workflow (no automatic migration).
 *
 * Access: read for all staff; queue-state writes require
 * admin / jurisdiction_manager (enforced by backend).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  RefreshCw, Search, Download, ExternalLink, Loader2, Plus,
  AlertTriangle, CheckCircle2, ClipboardList, UserCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { getUserFromToken } from '../utils/adminApi';
import './JurisdictionDirectory.css';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  pending: 'Pending',
  in_review: 'In Review',
  verified: 'Verified',
  assignment_created: 'Assignment Created',
};

const STATUS_COLORS = {
  pending: 'bg-slate-100 text-slate-700',
  in_review: 'bg-blue-50 text-blue-700',
  verified: 'bg-emerald-50 text-emerald-700',
  assignment_created: 'bg-emerald-100 text-emerald-800',
};

const TYPE_OPTIONS = ['township', 'borough', 'city', 'county', 'school_district'];

function openAssignmentsTab(jurisdictionId) {
  if (!jurisdictionId) return;
  const url = `/admin/jurisdictions?focus=${encodeURIComponent(jurisdictionId)}&tab=assignments`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function LegacyFallbackQueuePage() {
  const user = useMemo(() => getUserFromToken(), []);
  const canWrite = ['admin', 'jurisdiction_manager'].includes(user?.role);

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [county, setCounty] = useState('');
  const [jurType, setJurType] = useState('');
  const [missing, setMissing] = useState('');
  const [verify, setVerify] = useState('');
  const [status, setStatus] = useState('');
  const [assignedTo, setAssignedTo] = useState('');

  // Queue-state edit dialog
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({ assigned_to: '', status: 'pending', notes: '' });
  const [saving, setSaving] = useState(false);

  const buildParams = useCallback(() => {
    const p = new URLSearchParams({ state: 'PA' });
    if (search) p.append('search', search);
    if (county) p.append('county', county);
    if (jurType) p.append('jurisdiction_type', jurType);
    if (missing) p.append('missing', missing);
    if (verify) p.append('manual_verification', verify);
    if (status) p.append('status', status);
    if (assignedTo) p.append('assigned_to', assignedTo);
    return p;
  }, [search, county, jurType, missing, verify, status, assignedTo]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/legacy-fallback-audit?${buildParams()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setItems(data.items || []);
      setSummary(data.summary || null);
    } catch (e) {
      toast.error('Failed to load legacy fallback audit');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    const t = setTimeout(fetchItems, 300);
    return () => clearTimeout(t);
  }, [fetchItems]);

  const handleExport = async (format) => {
    try {
      const p = buildParams();
      p.append('format', format);
      const res = await fetch(`${API}/api/legacy-fallback-audit/export?${p}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `legacy_fallback_audit_${new Date().toISOString().slice(0, 10)}.${format}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Audit exported (${format.toUpperCase()})`);
    } catch {
      toast.error('Export failed');
    }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setEditForm({
      assigned_to: item.assigned_to || '',
      status: item.queue_status || 'pending',
      notes: item.queue_notes || '',
    });
  };

  const saveQueueState = async () => {
    if (!editItem) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/legacy-fallback-audit/${editItem.jurisdiction_id}`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      toast.success('Queue item updated');
      setEditItem(null);
      fetchItems();
    } catch (e) {
      toast.error(e.message || 'Failed to update queue item');
    } finally {
      setSaving(false);
    }
  };

  const kpis = [
    { label: 'Requiring Assignment', value: summary?.total ?? '—', icon: ClipboardList, color: 'text-blue-600 bg-blue-50' },
    { label: 'Missing Both Periods', value: summary?.missing_both ?? '—', icon: AlertTriangle, color: 'text-amber-600 bg-amber-50' },
    { label: 'Missing Current', value: summary?.missing_current ?? '—', icon: AlertTriangle, color: 'text-orange-600 bg-orange-50' },
    { label: 'Missing Delinquent', value: summary?.missing_delinquent ?? '—', icon: AlertTriangle, color: 'text-rose-600 bg-rose-50' },
    { label: 'Needs Manual Verify', value: summary?.manual_verification ?? '—', icon: UserCheck, color: 'text-purple-600 bg-purple-50' },
  ];

  return (
    <div className="p-6 max-w-[1500px] mx-auto" data-testid="legacy-fallback-queue-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Pending Collector Assignments</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            PA jurisdictions still resolving collectors from legacy directory data. Verify each collector,
            then use <strong>Create Assignment</strong> to migrate it to an official linked assignment. No automatic migration is performed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => handleExport('csv')} className="gap-1.5" data-testid="export-csv-btn">
            <Download className="w-4 h-4" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport('xlsx')} className="gap-1.5" data-testid="export-xlsx-btn">
            <Download className="w-4 h-4" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={fetchItems} className="gap-1.5" data-testid="refresh-queue-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {kpis.map(k => (
          <div key={k.label} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${k.color}`}>
              <k.icon className="w-4 h-4" />
            </div>
            <p className="text-2xl font-bold text-slate-900">{k.value}</p>
            <p className="text-xs text-slate-500 mt-0.5">{k.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <div className="relative col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search jurisdiction..." className="pl-9 h-9" data-testid="queue-search-input" />
        </div>
        <Input value={county} onChange={e => setCounty(e.target.value)} placeholder="County" className="h-9" data-testid="queue-county-input" />
        <Select value={jurType || 'all'} onValueChange={v => setJurType(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-9" data-testid="queue-type-select"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {TYPE_OPTIONS.map(t => <SelectItem key={t} value={t} className="capitalize">{t.replace('_', ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={missing || 'all'} onValueChange={v => setMissing(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-9" data-testid="queue-missing-select"><SelectValue placeholder="Missing Period" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any Missing</SelectItem>
            <SelectItem value="current">Missing Current</SelectItem>
            <SelectItem value="delinquent">Missing Delinquent</SelectItem>
            <SelectItem value="both">Missing Both</SelectItem>
          </SelectContent>
        </Select>
        <Select value={verify || 'all'} onValueChange={v => setVerify(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-9" data-testid="queue-verify-select"><SelectValue placeholder="Verification" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any Verification</SelectItem>
            <SelectItem value="yes">Manual Verify: Yes</SelectItem>
            <SelectItem value="no">Manual Verify: No</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status || 'all'} onValueChange={v => setStatus(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-9" data-testid="queue-status-select"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input value={assignedTo} onChange={e => setAssignedTo(e.target.value)} placeholder="Assigned to" className="h-9" data-testid="queue-assigned-input" />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="py-16 flex items-center justify-center"><Loader2 className="w-6 h-6 text-blue-500 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
            <p className="text-sm font-medium text-slate-600">No legacy fallbacks match the current filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="!bg-blue-600 !text-white px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Jurisdiction</th>
                  <th className="!bg-blue-600 !text-white px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">County</th>
                  <th className="!bg-blue-600 !text-white px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Legacy Collector</th>
                  <th className="!bg-blue-600 !text-white px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Missing / Category</th>
                  <th className="!bg-blue-600 !text-white px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Scope</th>
                  <th className="!bg-blue-600 !text-white px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Verify</th>
                  <th className="!bg-blue-600 !text-white px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Assigned / Status</th>
                  <th className="!bg-blue-600 !text-white px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map(item => (
                  <tr key={item.jurisdiction_id} className="hover:bg-slate-50 transition-colors" data-testid={`queue-row-${item.jurisdiction_id}`}>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-900">{item.jurisdiction_name}</p>
                      <p className="text-xs text-slate-400 capitalize">{(item.jurisdiction_type || '').replace('_', ' ')}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{item.county || <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-3">
                      <p className="text-slate-800">{item.legacy_current_collector.name || <span className="text-slate-300">No name</span>}</p>
                      <p className="text-xs text-slate-400">{item.legacy_current_collector.phone}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {item.missing_periods.map(p => (
                          <span key={p} className="inline-flex items-center px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 text-xs font-medium border border-amber-200">
                            {p} · {item.missing_assignment_category[p]}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 max-w-[180px]">{item.scope_requirement === 'None' ? <span className="text-slate-300">None</span> : item.scope_requirement}</td>
                    <td className="px-4 py-3">
                      {item.manual_verification_required ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 text-xs font-medium">Yes</span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-100 text-slate-500 text-xs font-medium">No</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-xs text-slate-700">{item.assigned_to || <span className="text-slate-300">Unassigned</span>}</p>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium mt-1 ${STATUS_COLORS[item.queue_status]}`}>
                        {STATUS_LABELS[item.queue_status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button
                        size="sm" variant="outline"
                        className="gap-1 h-8 border-blue-200 text-blue-700 hover:bg-blue-50"
                        onClick={() => openAssignmentsTab(item.jurisdiction_id)}
                        data-testid={`create-assignment-btn-${item.jurisdiction_id}`}
                        title="Open Collectors & Assignments for this jurisdiction"
                      >
                        <Plus className="w-3.5 h-3.5" /> Create Assignment <ExternalLink className="w-3 h-3" />
                      </Button>
                      {canWrite && (
                        <Button size="sm" variant="ghost" className="h-8 ml-1" onClick={() => openEdit(item)} data-testid={`edit-queue-btn-${item.jurisdiction_id}`}>
                          Manage
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Queue-state edit dialog */}
      <Dialog open={!!editItem} onOpenChange={(o) => !o && setEditItem(null)}>
        <DialogContent className="max-w-md" data-testid="queue-edit-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Manage Queue Item — {editItem?.jurisdiction_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Assigned To</Label>
              <Input value={editForm.assigned_to} onChange={e => setEditForm(f => ({ ...f, assigned_to: e.target.value }))} placeholder="Staff name or email" className="mt-1" data-testid="queue-edit-assigned-input" />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={editForm.status} onValueChange={v => setEditForm(f => ({ ...f, status: v }))}>
                <SelectTrigger className="mt-1" data-testid="queue-edit-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} rows={3} className="mt-1" placeholder="Verification notes..." data-testid="queue-edit-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditItem(null)}>Cancel</Button>
            <Button onClick={saveQueueState} disabled={saving} data-testid="queue-edit-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
